import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Download, FileText, TrendingUp, Package, DollarSign } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentCompanyId } from '../lib/company';
import DocumentActions from '../components/ui/document-actions';
import { toast } from 'sonner';

const PRESETS = [
  { label: 'Hoy', days: 0 },
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
  { label: 'Año', days: 365 },
];

export default function Reports() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState(getCurrentCompanyId() || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);

  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const isAdmin = user?.role_id === 'admin';
  const isVendedor = user?.role_id === 'vendedor';

  useEffect(() => { loadAll(); }, [filterCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      const cid = filterCompany || undefined;
      const [salesRes, prodsRes, compsRes] = await Promise.all([
        api.getSales({ company_id: cid }),
        api.getProducts({ company_id: cid }),
        api.getCompanies()
      ]);
      setSales(salesRes.data);
      setProducts(prodsRes.data);
      setCompanies(compsRes.data);
    } catch (e) {
      toast.error('SalesPRO: Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    if (days === 0) {
      from.setHours(0, 0, 0, 0);
    } else {
      from.setDate(from.getDate() - days);
    }
    setFromDate(from.toISOString().slice(0, 10));
    setToDate(to.toISOString().slice(0, 10));
  };

  const filtered = useMemo(() => {
    return sales.filter(s => {
      if (filterCompany && s.company_id !== filterCompany) return false;
      const created = new Date(s.created_at);
      if (fromDate && created < new Date(fromDate)) return false;
      if (toDate) {
        const end = new Date(toDate); end.setHours(23, 59, 59, 999);
        if (created > end) return false;
      }
      return true;
    });
  }, [sales, filterCompany, fromDate, toDate]);

  const totalSales = filtered.reduce((s, x) => s + (x.total || 0), 0);
  const totalCount = filtered.length;
  const avgTicket = totalCount > 0 ? totalSales / totalCount : 0;
  const byStatus = filtered.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  // Resumen por empresa (solo master/admin)
  const byCompany = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      if (!map[s.company_id]) map[s.company_id] = { count: 0, total: 0 };
      map[s.company_id].count += 1;
      map[s.company_id].total += s.total || 0;
    });
    return Object.entries(map).map(([cid, v]) => ({
      company_id: cid,
      name: companies.find(c => c.company_id === cid)?.name || cid,
      ...v
    })).sort((a, b) => b.total - a.total);
  }, [filtered, companies]);

  // Top productos
  const topProducts = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      (s.items || []).forEach(it => {
        if (!map[it.product_id]) map[it.product_id] = { qty: 0, revenue: 0, name: it.name };
        map[it.product_id].qty += it.quantity || 0;
        map[it.product_id].revenue += (it.price || 0) * (it.quantity || 0);
      });
    });
    return Object.entries(map).map(([pid, v]) => ({ product_id: pid, ...v })).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [filtered]);

  const downloadReport = async (kind) => {
    try {
      const res = kind === 'sales'
        ? await api.exportSalesReport(undefined, filterCompany || undefined)
        : await api.exportProductsReport(filterCompany || undefined);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${kind}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('SalesPRO: Reporte exportado');
    } catch (e) {
      toast.error('SalesPRO: Error al exportar reporte');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Reportes y Resúmenes</h1>
        <p className="text-sm text-[#64748B] mt-1">
          {isVendedor ? 'Resumen de tus propios movimientos' : 'Análisis de ventas e inventario por empresa y período'}
        </p>
      </div>

      {/* Filtros */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            {(isMaster || isAdmin || isVendedor) && companies.length > 1 && (
              <div>
                <Label className="text-xs">Empresa</Label>
                <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}
                  className="px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" data-testid="filter-company">
                  <option value="">Todas las empresas activas</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9" data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9" data-testid="filter-to" />
            </div>
            <div className="flex gap-1 flex-wrap">
              {PRESETS.map(p => (
                <Button key={p.label} size="sm" variant="outline" onClick={() => applyPreset(p.days)} data-testid={`preset-${p.days}`}>
                  {p.label}
                </Button>
              ))}
              <Button size="sm" variant="ghost" onClick={() => { setFromDate(''); setToDate(''); setFilterCompany(getCurrentCompanyId() || ''); }} data-testid="reset-filters">
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><DollarSign size={14} />{isVendedor ? 'Tus ventas' : 'Total facturado'}</div>
            <p className="text-2xl font-bold">${totalSales.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><FileText size={14} />Facturas</div>
            <p className="text-2xl font-bold">{totalCount}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><TrendingUp size={14} />Ticket promedio</div>
            <p className="text-2xl font-bold">${avgTicket.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><Package size={14} />Productos</div>
            <p className="text-2xl font-bold">{products.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Estados */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-4">
          <h3 className="font-heading font-bold mb-3">Ventas por estado</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {Object.entries(byStatus).map(([st, count]) => (
              <div key={st} className="bg-[#F8F9FA] rounded p-2 text-center">
                <p className="text-xs text-[#64748B] truncate">{st}</p>
                <p className="text-xl font-bold">{count}</p>
              </div>
            ))}
            {Object.keys(byStatus).length === 0 && (
              <p className="text-sm text-[#94A3B8] col-span-full">Sin datos en el período seleccionado</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resumen por empresa (master/admin) */}
      {(isMaster || isAdmin) && byCompany.length > 0 && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <h3 className="font-heading font-bold mb-3">Resumen por empresa</h3>
            <table className="w-full text-sm">
              <thead className="text-xs text-[#64748B] uppercase">
                <tr><th className="text-left p-2">Empresa</th><th className="text-right p-2">Facturas</th><th className="text-right p-2">Total</th></tr>
              </thead>
              <tbody>
                {byCompany.map(c => (
                  <tr key={c.company_id} className="border-t" data-testid={`company-summary-${c.company_id}`}>
                    <td className="p-2 font-medium">{c.name}</td>
                    <td className="p-2 text-right">{c.count}</td>
                    <td className="p-2 text-right font-bold">${c.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Top productos */}
      {topProducts.length > 0 && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <h3 className="font-heading font-bold mb-3">Top 10 productos del período</h3>
            <table className="w-full text-sm">
              <thead className="text-xs text-[#64748B] uppercase">
                <tr><th className="text-left p-2">Producto</th><th className="text-right p-2">Unidades</th><th className="text-right p-2">Ingresos</th></tr>
              </thead>
              <tbody>
                {topProducts.map(p => (
                  <tr key={p.product_id} className="border-t">
                    <td className="p-2 font-medium">{p.name}</td>
                    <td className="p-2 text-right">{p.qty}</td>
                    <td className="p-2 text-right font-bold">${p.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Botones de exportación */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => downloadReport('sales')} variant="outline" data-testid="export-sales-btn">
            <Download size={16} className="mr-2" /> Exportar ventas a Excel
          </Button>
          {(isMaster || isAdmin) && (
            <Button onClick={() => downloadReport('products')} variant="outline" data-testid="export-products-btn">
              <Download size={16} className="mr-2" /> Exportar inventario a Excel
            </Button>
          )}
        </div>
        <div>
          <p className="text-xs text-[#64748B] mb-1.5 font-medium uppercase tracking-wide">Reporte PDF de marca (con logo) · compartir</p>
          <DocumentActions
            fetchBlob={() => api.getBrandedReportPdf({
              type: 'sales',
              company_id: filterCompany || undefined,
              from_date: fromDate || undefined,
              to_date: toDate || undefined,
            })}
            filename={`SalesPRO_ventas_${(fromDate || 'hist')}.pdf`}
            title={`SalesPRO — Reporte de Ventas · Total $${totalSales.toFixed(2)} · ${totalCount} documentos`}
          />
        </div>
      </div>
    </div>
  );
}
